import styled from "@emotion/styled";
import { Box, Chip, Tooltip } from "@mui/material";
import {
	OrderCategory,
	OrderStatus,
	OrderStatusReason,
	OrderStatusReasonType,
	OrderStatusType
} from "types/declarations/enums.ts";
import { Theme } from "@emotion/react";

interface OrderSummaryChipsProps {
	chipTitle: string | undefined;
}

interface OrderSummaryCardDetailsProps {
	isMerchOrder?: boolean;
}

export const OrderSummaryCardWrapper = styled(Box)(({ theme }) => ({
	border: "1px solid rgba(0, 0, 0, 0.12)",
	backgroundColor: theme.project.colors.allColors.hex.grayscale.gray50,
	cursor: "pointer",
	padding: "16px",
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: "16px",
	width: "100%",
	boxSizing: "border-box",
	"&:hover": {
		backgroundColor: theme.project.colors.backgroundColors["card:hover"]
	},
	svg: {
		color: theme.project.colors.fontColors.secondary
	}
}));

export const OrderSummaryCardContent = styled.div({
	flex: 0.85,
	minWidth: 0
});

export const OrderSummaryCardHeader = styled.div({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	gap: "8px",
	marginBottom: "16px"
});

export const OrderSummaryCardJobName = styled.h1(({ theme }) => ({
	...theme.project.typography.heading.h6,
	margin: 0,
	color: theme.project.colors.fontColors.main
}));

export const OrderSummaryCardHeaderContents = styled.div(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: "16px",
	"> div:first-of-type": {
		height: "100%",
		display: "flex",
		alignItems: "center"
	},
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		gap: "8px",
		alignItems: "flex-start",
		"> div:first-of-type": {
			marginBottom: "8px"
		}
	}
}));

export const OrderSummaryCardChipList = styled.div(({ theme }) => ({
	display: "flex",
	gap: "16px",
	[theme.breakpoints.down("md")]: {
		flexWrap: "wrap",
		gap: "8px"
	}
}));

export const OrderSummaryCardTooltip = styled(Tooltip)({
	"&.MuiTooltip-tooltip": {
		"&.MuiTooltip-tooltipPlacementBottom": {
			marginTop: "4px"
		}
	},
	cursor: "pointer"
});

export const OrderSummaryCardTooltipText = styled.p({
	fontFamily: "canada-type-gibson, sans-serif",
	fontSize: "var(--font-size-1)",
	lineHeight: "var(--line-height-17)",
	margin: 0
});

export const OrderSummaryCardChip = styled(Chip, {
	shouldForwardProp: (prop) => prop !== "chipTitle"
})<OrderSummaryChipsProps>(({ chipTitle, theme }) => ({
	display: "flex",
	height: "24px",
	alignItems: "baseline",
	lineHeight: "18px",
	color: getChipColors(chipTitle, theme).color,
	border: `1px solid ${getChipColors(chipTitle, theme).borderColor}`,
	backgroundColor: getChipColors(chipTitle, theme).backgroundColor,
	"& .MuiChip-label": {
		fontSize: "15px",
		paddingTop: 2.5
	}
}));

export const OrderSummaryCardHeaderShippingDetails = styled.div(({ theme }) => ({
	display: "flex",
	gap: "8px",
	alignItems: "center",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		gap: "0px",
		alignItems: "flex-start"
	}
}));

export const OrderSummaryCardShippingDetailsContainer = styled.div(({ theme }) => ({
	span: {
		...theme.project.typography.subtitle.subtitle1,
		color: theme.project.colors.fontColors.secondary,
		fontSize: "14px",
		wordBreak: "break-all"
	}
}));

export const OrderSummaryCardDetails = styled.div<OrderSummaryCardDetailsProps>(({ theme, isMerchOrder }) => ({
	display: "grid",
	gridTemplateColumns: isMerchOrder ? "repeat(5, minmax(0, 1fr))" : "repeat(8, minmax(0, 1fr))",
	gap: "24px",
	div: {
		display: "flex",
		flexDirection: "column",
		minWidth: 0,
		overflow: "hidden",
		"span:first-of-type": {
			...theme.project.typography.subtitle.subtitle2,
			color: theme.project.colors.fontColors.main,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis"
		},
		"span:last-of-type": {
			...theme.project.typography.body.body2,
			color: theme.project.colors.fontColors.secondary,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis"
		}
	},
	[theme.breakpoints.down("md")]: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		gap: "4px",
		div: {
			flexDirection: "row",
			gap: "8px",
			alignItems: "center",
			overflow: "visible",
			"span:first-of-type": {
				whiteSpace: "normal",
				overflow: "visible"
			},
			"span:last-of-type": {
				whiteSpace: "normal",
				overflow: "visible"
			}
		}
	}
}));

const getChipColors = (chipLabel: string | undefined | null, theme: Theme) => {
	const { colors } = theme.project;
	const { fontColors, primaryColors, allColors } = colors;
	const { hex } = allColors;

	// default (order type)
	let color: string = fontColors.main;
	let borderColor: string = "none";
	let backgroundColor: string = hex.grayscale.gray300;

	if (!chipLabel) return { color, borderColor, backgroundColor };

	// order status
	if (Object.values(OrderStatus).includes(chipLabel as OrderStatusType)) {
		backgroundColor = "transparent";

		if (chipLabel === OrderStatus.SUBMITTED_WITH_ERRORS || chipLabel === OrderStatus.HOLD) {
			color = hex.orange.orange500;
			borderColor = hex.orange.orange500;
		} else {
			color = primaryColors.main;
			borderColor = primaryColors.main;
		}
		return { color, borderColor, backgroundColor };
	}

	// order status reason
	if (Object.values(OrderStatusReason).includes(chipLabel as OrderStatusReasonType)) {
		if (chipLabel === OrderStatusReason.DELIVERY_DATE_CHANGED || chipLabel === OrderStatusReason.PROCESSING_HOLD) {
			color = hex.orange.orange500;
			backgroundColor = hex.orange.orange50;
		} else {
			color = fontColors.label;
			backgroundColor = primaryColors.main;
		}
		return { color, borderColor, backgroundColor };
	}

	// order category (stock)
	if (chipLabel === OrderCategory.STOCK) {
		borderColor = hex.grayscale.gray400;
		backgroundColor = "transparent";
	}

	return { color, borderColor, backgroundColor };
};
